import { useEffect } from 'react';
import { depoimentosPalestra, FONTE_GOOGLE } from '../../mocks/depoimentos';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import AnimatedStatCard from '@/components/feature/AnimatedStatCard';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingCTA from '../../components/feature/FloatingCTA';
import Breadcrumb from '../../components/feature/Breadcrumb';

declare const gtag: (command: string, action: string, params?: Record<string, unknown>) => void;

// ─── SP State Coverage (ServiceArea schema) ──────────────────────────────────

const SP_ALL_CITIES = [
  { name: 'São Paulo', lat: -23.5505, lng: -46.6333 },
  { name: 'Campinas', lat: -22.9056, lng: -47.0608 },
  { name: 'São José dos Campos', lat: -23.1791, lng: -45.8872 },
  { name: 'Ribeirão Preto', lat: -21.1767, lng: -47.8202 },
  { name: 'Santos', lat: -23.9608, lng: -46.3339 },
  { name: 'Barueri', lat: -23.5112, lng: -46.8764 },
  { name: 'São Bernardo do Campo', lat: -23.6914, lng: -46.5647 },
  { name: 'Guarulhos', lat: -23.4531, lng: -46.5333 },
  { name: 'Jundiaí', lat: -23.1865, lng: -46.8845 },
];

// ─── Types ───────────────────────────────────────────────────────────────────

interface CityData {
  cityName: string;
  state: string;
  slug: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  phone: string;
  localKeywords: string[];
  heroTitle: string;
  heroSubtitle: string;
  localPresence?: string;
  localStats?: {
    clients: string;
    satisfaction: string;
  };
  /** Descontinuado: os depoimentos agora vêm de mocks/depoimentos (avaliações reais do Google). */
  localTestimonials?: Array<never>;
  localFAQs: Array<{
    question: string;
    answer: string;
  }>;
  seoTitle: string;
  seoDescription: string;
  relatedCities?: Array<{
    name: string;
    slug: string;
    region: string;
  }>;
}

interface CityLandingPageProps {
  cityData: CityData;
}

export default function CityLandingPage({ cityData }: CityLandingPageProps) {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: servicesRef, isVisible: servicesVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: testimonialsRef, isVisible: testimonialsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  // Dynamic SEO and Schema
  useEffect(() => {
    // Update document title
    document.title = cityData.seoTitle;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', cityData.seoDescription);
    }

    // Update geo tags
    const geoPosition = document.querySelector('meta[name="geo.position"]');
    if (geoPosition) {
      geoPosition.setAttribute('content', `${cityData.coordinates.lat};${cityData.coordinates.lng}`);
    }

    const geoRegion = document.querySelector('meta[name="geo.region"]');
    if (geoRegion) {
      geoRegion.setAttribute('content', `BR-${cityData.state}`);
    }

    const geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (geoPlacename) {
      geoPlacename.setAttribute('content', cityData.cityName);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    // Add LocalBusiness Schema
    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Palestra Silenciosa ${cityData.cityName}`,
      "description": cityData.seoDescription,
      "url": `${siteUrl}/${cityData.slug}`,
      "telephone": cityData.phone,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "São Paulo",
        "addressRegion": "SP",
        "addressCountry": "BR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": cityData.coordinates.lat,
        "longitude": cityData.coordinates.lng
      },
      "areaServed": {
        "@type": "City",
        "name": cityData.cityName
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": cityData.coordinates.lat,
          "longitude": cityData.coordinates.lng
        },
        "geoRadius": "50000"
      }
    };

    // ── SP State: expand areaServed + inject Service schema ─────────────────
    if (cityData.state === 'SP') {
      // Replace single-city areaServed with all SP cities
      schema.areaServed = SP_ALL_CITIES.map(c => ({
        "@type": "City",
        "name": c.name,
      }));
      delete schema.serviceArea;

      const spServiceAreaSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${siteUrl}/${cityData.slug}#service-area-sp`,
        "name": `Palestra Silenciosa — Cobertura ${cityData.cityName} e Estado de São Paulo`,
        "description": `Rede de atendimento de palestra silenciosa em ${cityData.cityName} e todas as regiões do estado de São Paulo: capital, Grande SP, ABCD, Alphaville, Campinas, Vale do Paraíba, Ribeirão Preto, Baixada Santista e interior logístico.`,
        "provider": { "@id": `${siteUrl}/#business` },
        "serviceType": "Locação de Equipamento de Áudio Sem Fio para Eventos",
        "url": `${siteUrl}/${cityData.slug}`,
        "areaServed": {
          "@type": "State",
          "name": "São Paulo",
          "containsPlace": SP_ALL_CITIES.map(c => ({
            "@type": "City",
            "name": c.name,
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": c.lat,
              "longitude": c.lng,
            },
          })),
        },
      };

      const existingSpService = document.querySelector('#city-service-area-sp-schema');
      if (existingSpService) existingSpService.remove();

      const spServiceScript = document.createElement('script');
      spServiceScript.id = 'city-service-area-sp-schema';
      spServiceScript.type = 'application/ld+json';
      spServiceScript.textContent = JSON.stringify(spServiceAreaSchema);
      document.head.appendChild(spServiceScript);
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
        { "@type": "ListItem", "position": 2, "name": `Palestra Silenciosa ${cityData.cityName}`, "item": `${siteUrl}/${cityData.slug}` }
      ]
    };

    const existingSchema = document.querySelector('#city-schema');
    if (existingSchema) existingSchema.remove();
    const existingBreadcrumb = document.querySelector('#city-breadcrumb-schema');
    if (existingBreadcrumb) existingBreadcrumb.remove();

    const schemaScript = document.createElement('script');
    schemaScript.id = 'city-schema';
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.id = 'city-breadcrumb-schema';
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    // FAQPage schema for local FAQs
    const faqPageSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": cityData.localFAQs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const existingFaq = document.querySelector('#city-faq-schema');
    if (existingFaq) existingFaq.remove();

    const faqScript = document.createElement('script');
    faqScript.id = 'city-faq-schema';
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqPageSchema);
    document.head.appendChild(faqScript);

    return () => {
      const schemaToRemove = document.querySelector('#city-schema');
      if (schemaToRemove) schemaToRemove.remove();
      const breadcrumbToRemove = document.querySelector('#city-breadcrumb-schema');
      if (breadcrumbToRemove) breadcrumbToRemove.remove();
      const faqToRemove = document.querySelector('#city-faq-schema');
      if (faqToRemove) faqToRemove.remove();
      const spServiceToRemove = document.querySelector('#city-service-area-sp-schema');
      if (spServiceToRemove) spServiceToRemove.remove();
    };
  }, [cityData]);

  const handleQuoteClick = () => {
    // GA4 event tracking with city label
    if (typeof gtag !== 'undefined') {
      gtag('event', 'click', {
        event_category: 'CTA',
        event_label: `City Landing - ${cityData.cityName}`,
        value: 1
      });
    }
  };

  const services = [
    {
      icon: 'ri-headphone-line',
      title: 'Palestras Silenciosas',
      description: `Sistema completo de fones sem fio para eventos corporativos em ${cityData.cityName}. Perfeito para conferências e workshops simultâneos.`,
    },
    {
      icon: 'ri-music-2-line',
      title: 'Festas Silenciosas',
      description: `Transforme festas em ${cityData.cityName} com até 10 canais musicais simultâneos. Zero reclamações de vizinhos.`,
    },
    {
      icon: 'ri-building-line',
      title: 'Eventos Corporativos',
      description: `Soluções personalizadas para convenções e feiras em ${cityData.cityName}, com equipe e equipamentos enviados de São Paulo.`,
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Workshops e Treinamentos',
      description: `Equipamentos para treinamentos e seminários em ${cityData.cityName} com tradução simultânea disponível.`,
    },
  ];

  return (
    <>
      <Header />
      <FloatingCTA />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen w-full flex items-center justify-center overflow-hidden" aria-label={`Palestra Silenciosa ${cityData.cityName} - Tecnologia de Áudio para Eventos`}>
        <div className="absolute inset-0">
          <img
            src="/img/stib/fone-detalhe-par.webp"
            alt={`Palestra Silenciosa ${cityData.cityName} - Evento corporativo com fones sem fio`}
            title={`Palestra Silenciosa ${cityData.cityName} - Tecnologia de Áudio Sem Fio`}
            className={`w-full h-full object-cover object-top transition-all duration-1000 ${heroVisible ? 'scale-100 opacity-100' : 'scale-110 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        {/* Breadcrumb — topo do hero */}
        <div className="absolute top-24 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb
              variant="light"
              items={[
                { label: 'Home', to: '/' },
                { label: 'Serviços', to: '/servicos' },
                { label: `Palestra Silenciosa ${cityData.cityName}` },
              ]}
            />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <header>
            <h1 className={`font-heading font-extrabold text-6xl md:text-8xl text-white mb-8 tracking-wide leading-tight transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {cityData.heroTitle}
            </h1>
          </header>
          <p className={`font-body font-light text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {cityData.heroSubtitle}
          </p>
          <Link
            to="/contato"
            onClick={handleQuoteClick}
            className={`inline-flex items-center gap-4 bg-primary text-black px-12 py-5 rounded-full font-body font-bold text-lg uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            aria-label={`Solicitar orçamento para palestra silenciosa em ${cityData.cityName}`}
          >
            SOLICITAR ORÇAMENTO - {cityData.cityName}
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full">
              <i className="ri-arrow-right-line text-black text-xl" aria-hidden="true"></i>
            </div>
          </Link>
        </div>
      </section>

      {/* Local Stats Section */}
      <section ref={statsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              nossa presença em {cityData.cityName}
            </h2>
            {cityData.localPresence && (
              <p className="font-body text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {cityData.localPresence}
              </p>
            )}
          </div>
          
          {cityData.localStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <AnimatedStatCard
              numericValue={parseInt(cityData.localStats.clients) || 0}
              suffix={cityData.localStats.clients.replace(/[0-9]/g, '')}
              label="Clientes Atendidos"
              delay={200}
              visible={statsVisible}
              numberColor="text-secondary"
              className="p-8 bg-gradient-to-br from-secondary/10 to-primary/10 rounded-3xl"
            />
            <AnimatedStatCard
              numericValue={parseInt(cityData.localStats.satisfaction) || 0}
              suffix={cityData.localStats.satisfaction.replace(/[0-9]/g, '')}
              label="Satisfação dos Clientes"
              delay={400}
              visible={statsVisible}
              numberColor="text-primary"
              className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-3xl"
            />
          </div>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase transition-all duration-700 ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              nossos serviços em {cityData.cityName}
            </h2>
            <p className={`font-body text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Soluções completas de tecnologia silenciosa para eventos em {cityData.cityName} e região metropolitana
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {services.map((service, index) => (
              <div
                key={index}
                className={`bg-white p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary flex-shrink-0 transition-transform duration-300 hover:scale-110">
                    <i className={`${service.icon} text-white text-3xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-secondary leading-tight">
                    {service.title}
                  </h3>
                </div>
                <p className="font-body text-base text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Testimonials */}
      <section ref={testimonialsRef} className="py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-white mb-6 lowercase transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              o que dizem nossos clientes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {depoimentosPalestra.slice(0, 6).map((d, index) => (
              <figure
                key={d.nome}
                className={`flex flex-col bg-white p-8 rounded-2xl shadow-xl transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${Math.min(index, 5) * 120}ms` }}
              >
                <div className="flex items-center mb-4" aria-label="5 de 5 estrelas">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <blockquote className="font-body text-gray-600 mb-6 leading-relaxed flex-1">{d.texto}</blockquote>
                <figcaption>
                  <h4 className="font-heading font-bold text-secondary">{d.nome}</h4>
                  {d.contexto && <p className="font-body text-sm text-gray-500">{d.contexto}</p>}
                </figcaption>
              </figure>
            ))}
          </div>

          <p className="mt-10 text-center font-body text-sm text-white/70">
            Depoimentos publicados por clientes no Google.{' '}
            <a href={FONTE_GOOGLE} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white">
              Ver no Google
            </a>
          </p>
        </div>
      </section>

      {/* Local FAQ Section */}
      <section ref={faqRef} className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              dúvidas frequentes - {cityData.cityName}
            </h2>
          </div>

          <div className="space-y-6">
            {cityData.localFAQs.map((faq, index) => (
              <div
                key={index}
                className={`bg-gray-50 rounded-2xl p-8 transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h4 className="font-heading font-bold text-xl text-secondary mb-4">
                  <a href={`#faq-${index}`} className="hover:underline">{faq.question}</a>
                </h4>
                <p className="font-body text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Cities Section (internal backlinks) */}
      {cityData.relatedCities && cityData.relatedCities.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block font-body text-sm font-semibold uppercase tracking-widest text-primary mb-3">
                Cobertura Estadual
              </span>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary mb-4 lowercase">
                também atendemos em são paulo
              </h2>
              <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Além de {cityData.cityName}, oferecemos tecnologia de palestra silenciosa em diversas cidades do estado de São Paulo
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {cityData.relatedCities.map((city) => (
                <Link
                  key={city.slug}
                  to={city.slug}
                  className="group bg-white rounded-2xl p-5 border border-gray-100 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <p className="font-heading font-bold text-secondary group-hover:text-primary transition-colors duration-300 text-base mb-1">
                    {city.name}
                  </p>
                  <p className="font-body text-xs text-gray-400 group-hover:text-gray-500 transition-colors duration-300">
                    {city.region}
                  </p>
                </Link>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/sao-paulo"
                className="inline-flex items-center gap-2 text-primary font-body font-semibold hover:underline cursor-pointer"
              >
                Ver todas as cidades de São Paulo
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-primary"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* City-specific CTA Section */}
      <section ref={ctaRef} className="relative w-full h-[600px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/stib/fone-detalhe-mao.webp"
            alt={`Transforme seu evento em ${cityData.cityName}`}
            className={`w-full h-full object-cover object-top transition-all duration-1000 ${ctaVisible ? 'scale-100' : 'scale-110'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 via-secondary/40 to-secondary/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 w-full">
          <div className="flex flex-col md:flex-row items-end justify-between gap-12">
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-white lowercase leading-tight transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
              transforme seu evento<br />em {cityData.cityName}
            </h2>

            <div className={`flex flex-col items-end gap-8 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
              <p className="font-body font-light text-lg text-white text-right max-w-md leading-relaxed">
                <strong>Apenas 2 vagas disponíveis</strong> para eventos em {cityData.cityName} este mês. Tecnologia de ponta sem interferências.
              </p>
              <Link
                to="/contato"
                onClick={handleQuoteClick}
                className="inline-flex items-center gap-4 bg-white text-secondary px-10 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer group"
              >
                ORÇAMENTO URGENTE - {cityData.cityName}
                <div className="w-10 h-10 flex items-center justify-center bg-black rounded-full transition-transform duration-300 group-hover:translate-x-1">
                  <i className="ri-arrow-right-line text-white text-xl"></i>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}